import { supabase } from '@/lib/supabase/client';
import { deleteFile, getSignedUrl, uploadDealDocument } from '@/lib/services/storage';
import type { DealDocument, DealDocumentInsert, DealDocumentType } from '@/types/deal-documents';

async function getCurrentUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

async function attachContractToLocationIfBlank(input: {
  locationId: string | null;
  documentType: DealDocumentType;
  storagePath: string;
}): Promise<void> {
  if (!input.locationId || !['contract', 'sla'].includes(input.documentType)) {
    return;
  }

  const { data: location, error: locationError } = await supabase
    .from('locations')
    .select('contract_file_path, contract_file_url')
    .eq('id', input.locationId)
    .maybeSingle();

  if (locationError || !location || location.contract_file_path || location.contract_file_url) {
    return;
  }

  const { error } = await supabase
    .from('locations')
    .update({ contract_file_path: input.storagePath, contract_file_url: null })
    .eq('id', input.locationId);

  if (error) {
    console.error('Error attaching deal document to location contract field:', error);
  }
}

export const dealDocumentsService = {
  async getByDealId(dealId: string): Promise<DealDocument[]> {
    const { data, error } = await supabase
      .from('deal_documents')
      .select('*')
      .eq('deal_id', dealId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching deal documents:', error);
      return [];
    }

    return (data ?? []) as DealDocument[];
  },

  async create(input: DealDocumentInsert): Promise<DealDocument> {
    const userId = await getCurrentUserId();
    const { data, error } = await supabase
      .from('deal_documents')
      .insert({
        deal_id: input.deal_id,
        location_id: input.location_id ?? null,
        document_type: input.document_type ?? 'other',
        storage_bucket: input.storage_bucket ?? 'contracts',
        storage_path: input.storage_path,
        file_name: input.file_name,
        description: input.description ?? null,
        mime_type: input.mime_type ?? null,
        file_size: input.file_size ?? null,
        created_by: userId,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating deal document:', error);
      throw new Error(`Failed to save deal document: ${error.message}`);
    }

    return data as DealDocument;
  },

  async upload(
    dealId: string,
    file: File,
    description: string | null = null,
    locationId: string | null = null
  ): Promise<DealDocument> {
    const upload = await uploadDealDocument(dealId, file);
    const document = await this.create({
      deal_id: dealId,
      location_id: locationId,
      document_type: 'other',
      storage_bucket: 'contracts',
      storage_path: upload.path,
      file_name: file.name,
      description,
      mime_type: file.type || null,
      file_size: file.size,
    });

    await attachContractToLocationIfBlank({
      locationId,
      documentType: document.document_type,
      storagePath: document.storage_path,
    });

    return document;
  },

  async getDownloadUrl(document: DealDocument, expiresIn = 3600): Promise<string> {
    return getSignedUrl(document.storage_bucket, document.storage_path, expiresIn);
  },

  async remove(document: DealDocument): Promise<void> {
    const { error } = await supabase.from('deal_documents').delete().eq('id', document.id);

    if (error) {
      console.error('Error deleting deal document row:', error);
      throw new Error(`Failed to remove deal document: ${error.message}`);
    }

    try {
      await deleteFile(document.storage_bucket, document.storage_path);
    } catch (error) {
      console.error('Deal document row removed but storage delete failed:', error);
    }
  },
};
